// Module 04: Story Structure That Works (id: story-anatomy-context)
// The Art of Story - Learning Module
// The course's own five-part structure for personal narratives, adapted for audiences

export const storyModule04Content = {
  id: 'story-anatomy-context',
  title: 'Story Structure That Works',
  subtitle: 'A core story structure, adapted for any audience or situation',
  description: 'Learn a core five-part structure for personal stories, then learn to adapt it strategically for job interviews, first dates, dinner parties, presentations, and every context in between.',
  duration: '38 minutes',
  image: '/images/mod-04-structure.png',

  content: {
    overview: `Picture someone who crashes and burns at a job interview with the exact same story she's been telling brilliantly at dinner parties for months.

The story is solid: how she navigated a toxic workplace, stood up to a bullying manager, and eventually left to preserve her mental health. At dinner parties, people lean in. They nod. They share their own toxic workplace stories. She feels seen.

At the interview, she tells the same story, with the same structure, the same emotional beats and the same punchline about prioritising mental health. The interviewers' faces go blank. Polite but distant. She knows immediately she's miscalculated.

"What did I do wrong?" she asks a friend afterwards. "It's a good story. People love that story."

"It is a good story," the friend says. "But you told it in the wrong shape for the wrong room."

What happened: **she used dinner party anatomy in a job interview context.**

At dinner parties, she'd emphasised the emotional journey: the anxiety attacks, the crying in the bathroom, the moment she finally snapped at her boss. That's what made it compelling for friends. It's vulnerable, relatable, human.

At the interview, she needed to emphasise problem-solving, boundary-setting, and professional growth. Same core event. Completely different anatomy. Different context.

**What most stories share:** whether it's a two-minute anecdote at a networking event or a ten-minute vulnerability share with a close friend, a personal story usually has the same bones. The five-part structure we teach is setup, catalyst, tension, climax, resolution. It's our own craft model, and it overlaps with what linguists William Labov and Joshua Waletzky (1967) found in everyday spoken stories: an orientation, a complicating action, an evaluation of why it matters, a resolution and a coda. It's not a formula; it's a skeleton.

But most people miss that **context determines which parts you emphasise, which you compress, and which you skip entirely.**

The story about overcoming a career setback works brilliantly at a job interview when you emphasise problem-solving and resilience. The same story works at a dinner party when you emphasise the absurd moment your boss called you into the office whilst you were eating a massive sandwich. And it works differently again on a first date when you emphasise what you learnt about yourself.

Same event. Different anatomy. Different context.

This module teaches you both: the **core structure** that makes stories work, and the **context adaptation** that makes them land in the actual room you're in.

Because you already have good stories. You just might be telling them in the wrong shape for where you are.`,

    learningObjectives: [
      'Understand the five-part story structure this course teaches (setup, catalyst, tension, climax, resolution) and why so many compelling personal narratives fit it',
      'Diagnose why your stories sometimes land and sometimes don\'t by identifying which structural elements are missing or misplaced',
      'Adapt the same core story for different contexts (professional, social, intimate, public) by emphasising different structural elements',
      'Strategically choose ascending, descending, or oscillating narrative patterns based on audience, purpose, and context',
      'Master story length adaptation: telling the same story compellingly in 2 minutes, 5 minutes, or 10 minutes, depending on the situation',
    ],

    sections: [
      {
        id: 'universal-anatomy',
        title: 'The Core Anatomy: Every Story Needs a Skeleton',
        duration: '8 minutes',
        content: {
          intro: `Imagine a mate telling you about his weekend. It goes like this:

"Friday night we went to that new bar on Smith Street. Had a few drinks. Saturday we slept in, then went to the market. Got some good cheese. Sunday we just hung around the house. Watched some Netflix. Pretty chill weekend actually."

You nod politely. Your brain has already moved on to what you're having for lunch.

Later that night, another friend tells you about HER weekend:

"So Friday, I'm meeting this guy from Hinge for the first time, right? I get to the bar twenty minutes early because I'm anxious. Order a wine. Spill it on my white shirt. Immediately. Before he even arrives. I'm standing in the bathroom trying to dry myself with those useless hand dryers when he texts: 'I'm here, at the bar.' I had to walk out there looking like I'd been in a water fight. He took one look at me and just started laughing. Not mean laughing. Genuine, warm laughing. And I thought, 'Okay, this might actually be okay.' We're going out again on Thursday."

You'd remember every detail of that story. You might even tell it to someone else the next day.

Same basic event: went somewhere, thing happened, felt a way about it. But one story evaporates instantly and one sticks like velcro.

The difference? The second story has **structure**. Setup, catalyst, tension, climax, resolution. That's no accident. It's the anatomy we use throughout this module, and you'll find it in most stories that grip you.

Before we talk about adapting stories for context, you need to understand this architecture. Because it's not about being formulaic. It's about understanding why some stories grip us and others drift past without impact.`,

          mainContent: `**The Five-Part Story Structure**

Whether you're telling a story about a first date disaster, a career pivot, or a family conflict, most compelling personal narratives fit this structure:

**1. SETUP (The Normal World)**

This is your "before" state. What was life like before the thing happened?

The setup does three jobs:
- **Orients the listener:** Where are we? When is this?
- **Establishes stakes:** What matters in this world?
- **Creates contrast:** So we can measure change later

**Example (job loss story):**
"I'd been at the same company for 12 years. I knew everyone's coffee order. I could navigate every political landmine. It was comfortable. Maybe too comfortable."

Notice what this setup does: It's not just "I had a job." It establishes routine, comfort and familiarity, which will contrast sharply with what comes next.

**Common setup mistakes:**
❌ Too long (spending 3 minutes on setup in a 5-minute story)
❌ Too vague ("Things were fine")
❌ Skipping it entirely ("So I got made redundant...")

**2. CATALYST (The Inciting Incident)**

Something changes. This is the moment that disrupts the setup and forces you into new territory.

In many personal stories the catalyst is external: something happens *to* you that you didn't choose. It can also be a decision you make, like quitting a job in the business story later in this module.

**Example:**
"Then one Tuesday morning, my boss called me into her office. By Tuesday afternoon, my role had been made redundant."

The catalyst should be clear and specific. Vague catalysts ("Things started to change...") don't create narrative momentum.

**3. TENSION (The Struggle)**

This is the meat of the story. After the catalyst, what happened? What did you try? What got in the way?

Tension comes from:
- **Obstacles:** What blocked you?
- **Attempts:** What did you try?
- **Complications:** What made it harder?
- **Emotional stakes:** What were you feeling?

**Example:**
"The first month was brutal. I applied to 30 jobs and heard nothing back. I'd lie awake at 3am doing financial calculations. Then I finally got an interview, and completely froze when they asked me to describe my strengths. I couldn't even remember what I was good at anymore."

Notice: multiple attempts (applications, interview), obstacles (silence, freezing), emotional stakes (3am worry, forgetting strengths).

This is where most people under-develop their stories. They rush from catalyst to resolution:
"I got made redundant. It was hard. But I found a new job."

That's not a story. That's a summary. **Tension is where the story lives.**

[INSIGHT: Why Tension Matters | Uri Hasson's neural coupling work (Module 2) established that listener brains track a storyteller's brain during narrative, and that stronger coupling goes with better comprehension. It is often stretched one step further, into a claim that coupling spikes specifically during tension, and that step isn't Hasson's. What his study does show is that coupling depends on the listener understanding the story: it disappeared when listeners heard a story in a language they didn't know. Tension is how you keep someone following. They stay because they don't yet know how it comes out. Compress the tension and you haven't just lost drama; you've removed the reason to keep listening.]

**4. CLIMAX (The Peak Moment)**

This is the moment of highest intensity or realisation. Something shifts.

The climax can be:
- **External:** A specific event (getting the job offer, the confrontation, the decision)
- **Internal:** A realisation or shift in perspective ("That's when I realised...")

**Example:**
"Then a former colleague called and said, 'You know you're applying for the wrong roles, right? You're trying to be who you were, not who you could be.' That conversation lasted 20 minutes, but it shifted everything."

The climax doesn't have to be huge or dramatic. It just needs to be the moment where something changes direction.

**5. RESOLUTION (The After State)**

What's different now? Where are you after all of this?

The resolution shows:
- **External change:** What actually happened?
- **Internal change:** How are you different?
- **Meaning:** What does this mean for you now?

**Example:**
"I'm doing contract work now, which isn't what I imagined at 52. But I'm learning things I'd never have touched in my old role. I don't have it all figured out, but I'm not trying to get back to comfortable anymore. Comfortable was keeping me small."

Notice this is an **oscillating resolution** (from Module 3). It acknowledges both difficulty ("not what I imagined") and growth ("learning things," "comfortable was keeping me small").

[COMPARE: Summary | "I got made redundant at 52, which was hard, but I found contract work and I'm learning new things." | Story | Uses all five elements: Setup (12 years, comfortable), Catalyst (Tuesday redundancy), Tension (30 applications, freezing in interview, 3am worry), Climax (colleague's call: "You're applying for the wrong roles"), Resolution (contract work, learning, not trying to get back to comfortable). The structure creates forward momentum and emotional engagement that summary can't achieve.]

**The Core Pattern: Status Quo → Disruption → Struggle → Shift → New Status Quo**

Most compelling personal stories follow this arc, whether they last 2 minutes or 20.

The power isn't in the formula. It's in understanding the anatomy so you can diagnose why your stories sometimes fall flat.

Usually, it's because you're missing tension (jumping from catalyst to resolution) or spending too long on setup.`,

          practicalApplication: `**Quick Story Anatomy Audit**

Think of a story you tell about yourself: something that happened to you that you share with others.

Now map it onto the five-part structure:

1. **Setup:** What was the "before" state?
2. **Catalyst:** What disrupted it?
3. **Tension:** What obstacles, attempts, complications followed?
4. **Climax:** What was the peak moment or shift?
5. **Resolution:** What's the "after" state?

If any element is missing or underdeveloped, that's likely why the story doesn't land as well as you'd like.

Most commonly:
- **Tension is too short** (you're summarising instead of storytelling)
- **Setup is too long** (you're boring people before the story starts)
- **Climax is vague** (there's no clear peak moment)

Identifying the weak point is most of the work of fixing the story.`,
        },
      },

      {
        id: 'context-adaptation',
        title: 'Context Is Everything: The Same Story, Five Different Ways',
        duration: '9 minutes',
        content: {
          intro: `Now that you understand the core anatomy, the interesting part begins: **you don't tell the story the same way in every context**.

The same core event (let's say, "I quit my corporate job to start a business that failed") works brilliantly in multiple contexts. But the *structure*, *emphasis*, and *details* shift based on who you're talking to and why.

Let's map this out.`,

          mainContent: `**The Core Event:**
"I quit my corporate job to start a business that failed after 18 months."

This event can be told five very different ways depending on context.

---

**CONTEXT 1: Job Interview**

**Audience expectation:** Professionalism, competence, learning from failure, resilience
**Duration:** 2-3 minutes
**Pattern:** Oscillating (acknowledge failure + demonstrate growth)
**Emphasis:** Problem-solving, learning, what you'd do differently

**How you'd tell it:**

*Setup (brief):* "I spent eight years in corporate strategy, which taught me rigorous analysis and planning."

*Catalyst:* "I saw a gap in the market for sustainable packaging and decided to start a business addressing it."

*Tension (compressed, focus on actions):* "I spent 18 months building the product, securing initial clients, and iterating based on feedback. What I underestimated was cash flow timing: clients paid on 60-day terms, but I had immediate costs."

*Climax:* "At month 16, I realised I'd either need significant external funding or I'd need to wind down. I chose to wind down strategically rather than accumulate debt."

*Resolution (emphasise learning):* "It was a failure by the obvious metric: the business didn't survive. But I learnt more in those 18 months than in eight years of corporate strategy. I understand cash flow viscerally now. I understand customer development. And I learnt I can handle uncertainty and difficult decisions. Those skills directly inform how I'd approach this role."

**What you're doing:**
- Showing competence (rigorous planning, client development)
- Owning failure without dwelling on it
- Demonstrating learning and resilience
- Connecting learning to the role you're interviewing for

---

**CONTEXT 2: Dinner Party**

**Audience expectation:** Entertainment, relatability, humour
**Duration:** 3-5 minutes
**Pattern:** Oscillating with humour
**Emphasis:** The absurd moments, human details, self-aware irony

**How you'd tell it:**

*Setup (set the scene):* "So, five years ago, I had this moment of entrepreneurial delusion. You know the kind, where you're convinced you're the only person who's ever thought of sustainable packaging?"

*Catalyst:* "I quit my extremely comfortable corporate job to save the world with cardboard. My family's faces when I announced this were... a picture."

*Tension (focus on absurd/human moments):* "Month three, I'm in my garage at 2am, genuinely trying to fold cardboard into shapes that won't collapse when you put food in them. My neighbour called the council because she thought I was building something suspicious. Month eight, I finally get my first big client meeting, and I've got prototype boxes lined up on the boardroom table like I'm on Dragons' Den. The MD picks one up, puts an apple in it, and the bottom falls out. Just... straight through onto the table."

*Climax:* "Month 16, I'm sitting with my accountant (who, I should mention, has been giving me worried looks for six months) and she very gently says, 'You know this isn't working, right?'"

*Resolution (self-aware):* "I closed the business, went back to corporate strategy, and now I have a very expensive education in what not to do. My garage still smells like cardboard. But I can say I tried, which genuinely matters to me, even if the execution was... optimistic."

**What you're doing:**
- Making it entertaining (absurd details, humour)
- Making yourself the butt of the joke (self-aware irony)
- Staying relatable (we've all had optimistic failures)
- Keeping it light (this is dinner, not therapy)

---

**CONTEXT 3: First Date**

**Audience expectation:** Authenticity, self-awareness, values, not oversharing
**Duration:** 3-4 minutes
**Pattern:** Oscillating (honest but not heavy)
**Emphasis:** What it revealed about your values, who you are

**How you'd tell it:**

*Setup:* "I spent my twenties climbing the corporate ladder: good salary, clear progression, all of that. But I kept feeling like I was solving the wrong problems."

*Catalyst:* "So at 32, I quit to start a business focussed on sustainable packaging. It felt like the first time I was doing something that actually aligned with what I cared about."

*Tension (focus on emotional honesty, not details):* "The first year was exhilarating and terrifying in equal measure. I'd wake up excited about the work and anxious about money. I learnt how little I actually knew about running a business. I made a lot of mistakes, some funny, some expensive."

*Climax:* "By month 18, it was clear the business wasn't going to survive. I had to decide whether to keep fighting or admit it wasn't working."

*Resolution (emphasise values and learning):* "I closed it. That was three years ago. It stung. It still does a bit. But I learnt that I need work that feels meaningful, even if I'm not running my own show. And I learnt I'm willing to risk comfort for something I care about, which I didn't know about myself before. That matters more to me than whether the business succeeded."

**What you're doing:**
- Showing values (meaningful work, sustainability)
- Demonstrating self-awareness (admitting mistakes, acknowledging ongoing feelings)
- Revealing character (willing to take risks, handle failure)
- Appropriate vulnerability (honest but not oversharing about financial stress or emotional difficulty)

---

**CONTEXT 4: Close Friend Over Coffee**

**Audience expectation:** Real vulnerability, emotional honesty, processing
**Duration:** 5-10 minutes
**Pattern:** Oscillating (acknowledging ongoing struggle)
**Emphasis:** Feelings, doubts, meaning-making

**How you'd tell it:**

*Setup:* "You remember how excited I was when I quit to start the packaging business? I really thought I'd figured out my path."

*Catalyst:* "When I left corporate, I felt like I was finally becoming who I was supposed to be."

*Tension (detailed, emotionally honest):* "But month six, I started having these moments of panic. Like, 'What have I done?' I'd put all my savings in. I wasn't seeing my friends because I was working 80-hour weeks. My relationship was strained because I was stressed all the time. And the business... it was fine, but it wasn't growing fast enough. I kept thinking, 'If I just work harder, if I just figure out the right strategy...' But there's only so long you can outwork a fundamentally difficult market."

*Climax:* "The moment I knew it was over was sitting with my accountant and realising I had maybe three months of runway left. I could feel my heart pounding. I was so angry at myself. Like, how did I not see this coming?"

*Resolution (honest, ongoing):* "I closed it three years ago. I'm back in corporate now, which feels like going backwards some days. I'm proud I tried, and I learnt a tonne. But I'd be lying if I said I'm totally over it. Sometimes I see other people's businesses taking off and I think, 'Why couldn't I make it work?' I'm still processing what it means that I failed at the thing I thought was my purpose. But I'm getting there."

**What you're doing:**
- Real emotional honesty (panic, anger, ongoing struggle)
- Detailed tension (the human cost, not just business metrics)
- Admitting you don't have it figured out
- Trusting your friend with unresolved feelings

---

**CONTEXT 5: Professional Talk/Presentation**

**Audience expectation:** Expertise, takeaways, teaching
**Duration:** 5-7 minutes (within a longer talk)
**Pattern:** Oscillating (failure + insight)
**Emphasis:** Lessons learned, frameworks, what the audience can apply

**How you'd tell it:**

*Setup:* "Five years ago, I did what a lot of strategy consultants dream about: I left corporate to start my own business. I had a solid plan, market research, financial projections. I felt prepared."

*Catalyst:* "What I didn't have was an understanding of cash flow timing in B2B sales."

*Tension (focus on the lesson):* "I secured clients. I delivered the product. Everything was working, except that my clients paid on 60-day terms and I had immediate costs. For 18 months, I was constantly chasing invoices whilst trying to deliver the next project. The business was growing on paper, but I was haemorrhaging cash in reality."

*Climax:* "Month 18, I had to make a choice: take on significant debt to bridge the cash flow gap, or wind down strategically."

*Resolution (teaching moment):* "I wound down. I learnt a few things I'd encourage you to think about if you're considering a similar path. Cash flow timing can sink a business with a perfectly good product. Revenue on paper means nothing if you can't make payroll. And 'passion' doesn't override basic financial mechanics. I see a lot of people making the same mistake I did: confusing a viable product with a viable business. Those are two different things. So if there's one thing you take from this story, it's this: before you quit your job for your brilliant idea, map your cash flow month by month for 24 months. Assume everything takes twice as long as you think. Because it will."

**What you're doing:**
- Positioning yourself as someone with hard-won expertise
- Extracting the lesson clearly
- Giving the audience something actionable
- Framing failure as valuable learning (credibility builder)

---

**Same Event. Five Different Structures.**

Notice what changed:
- **Duration:** 2 minutes (interview) to 10 minutes (close friend)
- **Details:** Absurd cardboard moments (dinner party), emotional panic (close friend), financial specifics (professional talk)
- **Tone:** Humorous (dinner party), vulnerable (close friend), instructive (professional talk)
- **Emphasis:** Learning (interview), entertainment (dinner party), values (first date), processing (close friend), teaching (professional talk)
- **Resolution:** What you learnt (interview), self-aware humour (dinner party), values revealed (first date), ongoing struggle (close friend), actionable lesson (professional talk)

The anatomy is the same (setup, catalyst, tension, climax, resolution). But the *shape* adapts to the room.`,

          practicalApplication: `**Context Adaptation Exercise**

Choose one story from your life: something that happened that you tell in different contexts.

Map how you'd tell it in three different contexts:

1. **Professional context** (job interview, networking event)
   - What would you emphasise?
   - What details would you include/exclude?
   - What resolution would you land on?

2. **Social context** (dinner party, casual conversation)
   - What would make it entertaining?
   - Where's the humour or relatability?
   - How would you keep it light?

3. **Intimate context** (close friend, partner, therapist)
   - What emotional honesty would you include?
   - What are you still processing?
   - What vulnerability is appropriate here?

Notice: Same event. Different anatomy. Different impact.

You're not being fake. You're being contextually appropriate. That's communication skill, not manipulation.`,
        },
      },

      {
        id: 'strategic-pattern-choice',
        title: 'Choosing Your Narrative Pattern Strategically',
        duration: '7 minutes',
        content: {
          intro: `Say you've just finished the last module and a question is nagging at you: "I get it: oscillating narratives tell the fuller story. So should I just... never tell ascending or descending stories anymore? Am I doing it wrong if I keep things light at a party?"

No. Keeping things light at a party is exactly right. Not every story needs to be oscillating.

"But Module 3 said..."

Module 3 made the case for oscillating narratives. But context matters. If someone asks "How was your weekend?" at the coffee machine on Monday morning, they're not looking for an oscillating narrative about your complicated relationship with your mother. They're making small talk. An ascending "Great, thanks!" is perfect.

It's a good question, though.

In Module 3, you learnt about ascending, descending, and oscillating narratives. Now let's talk about when to use each pattern **strategically**, based on context and purpose.

Because not every story should be oscillating. Sometimes you want a clean ascending narrative. Sometimes a descending story is exactly right. The skill is knowing which pattern serves the moment you're actually in.`,

          mainContent: `**When to Use Each Narrative Pattern**

**ASCENDING NARRATIVES: "It Got Better"**

**Best contexts:**
- Small talk and social lubrication
- Quick wins and confidence building
- When you want to keep things light
- Professional contexts where optimism is valued (sales, leadership)

**Example situations:**
- Networking: "How's the new role?" → "It's been great. I'm learning heaps and the team is brilliant."
- Dinner party: "How was your holiday?" → "Fantastic. We found this little village that wasn't in any guidebook. Best trip we've ever done."
- Team meeting: "How's the project going?" → "We hit a few snags early on, but we've sorted them and we're ahead of schedule now."

**Why it works here:**
People aren't expecting or wanting depth. They're engaging in social ritual, building rapport, or needing confidence from leadership. An ascending narrative gives them what they came for.

**When ascending fails:**
- Job interviews (feels performative: "everything's always been perfect!")
- Intimate relationships (feels like you're hiding)
- Situations requiring vulnerability or depth

---

**DESCENDING NARRATIVES: "It Got Worse" or "It's Still Hard"**

**Best contexts:**
- When you're seeking support or empathy
- Processing difficulty with trusted people
- Advocacy or raising awareness about systemic issues
- Appropriate venting with boundaries

**Example situations:**
- Close friend: "I'm really struggling with this redundancy. I thought I'd bounce back faster, but I'm still waking up anxious every morning."
- Therapy: "The relationship ended six months ago and I still feel stuck. I can't seem to move forward."
- Advocacy: "The current system leaves single parents without adequate support. I'm living it, and it's unsustainable."

**Why it works here:**
You're not looking for solutions or trying to perform resilience. You're acknowledging real, ongoing difficulty. The people you're talking to have the capacity and context for this honesty.

**When descending fails:**
- Job interviews (signals you're stuck, not resilient)
- First dates (oversharing, emotional dumping)
- Professional settings where you need to demonstrate capability
- Repeated use with the same people (becomes a fixed identity)

[INSIGHT: The Venting Boundary | Venting has a worse reputation in the research than it does in everyday life, and the distinction is the useful part. In Brad Bushman's 2002 experiment, angry people who hit a punching bag while thinking about the person who provoked them came out angrier and more aggressive than people who did nothing. Amanda Rose's research on co-rumination (2002) found that children and teenagers who go over problems again and again with a friend tend to have closer friendships and also more symptoms of depression and anxiety. Our reading: what helps isn't the venting itself so much as the meaning-making. So: time-limited, with someone who has capacity to hold it, and going somewhere. Descending narratives are appropriate when: (1) the listener has consented to this level of honesty, (2) you're seeking empathy, not solutions, and (3) this isn't your only narrative about yourself. If every story you tell ends at the low point, check whether you've started to believe no new chapter is possible: that's what Mark Freeman calls narrative foreclosure (Module 3).]

---

**OSCILLATING NARRATIVES: "It Was Hard AND I'm Learning"**

**Best contexts:**
- Job interviews (demonstrates resilience and learning)
- First dates (authentic without oversharing)
- Parenting (modelling healthy difficulty processing)
- Professional talks (credibility + teaching)
- Most situations requiring both honesty and hope

**Example situations:**
- Job interview: "I failed to meet a major deadline last year, which was a genuine wake-up call. I'd been juggling too many priorities without adequate systems. I implemented project management software and started weekly check-ins with my team. I haven't missed a deadline since, and my team's told me the check-ins have improved their clarity as well."

- First date: "My last relationship ended about a year ago. It was painful, and I'm still learning from it. But I'm clearer now about what I need in a relationship and what I can offer. I'm not rushing into anything, but I'm open to seeing where this goes."

- Talking with your child: "Grandma was really sick when I was your age, which was scary. Our family pulled together. We took turns visiting her, and we helped Grandpa with cooking. She got better eventually, but it took months. I learnt that families can handle hard things when everyone helps."

**Why it works broadly:**
Oscillating narratives are honest (they acknowledge difficulty) AND forward-moving (they include response and learning). They build trust without overwhelming. They're appropriate in more contexts than ascending or descending narratives because they hold complexity.

**When oscillating might be too much:**
- Very casual social settings (small talk doesn't need this depth)
- When someone asks a simple question and you read it as an invitation for depth (sometimes "How are you?" just means "Hello")

---

**Strategic Pattern Switching Within the Same Story**

Sometimes you tell the same event using different patterns *within the same conversation*, depending on how it unfolds.

**Example: The redundancy story**

**Opening (ascending, testing the waters):**
"I was made redundant last year. It was rough at first, but I'm in a good place now."

*If the other person engages with curiosity:*

**Middle (oscillating, adding depth):**
"Actually, it was harder than I'm making it sound. I struggled for months. But I've learnt a lot about what I actually want from work, which I wouldn't have questioned otherwise."

*If they're a close friend and you need support:*

**Deeper (descending, appropriate vulnerability):**
"Can I be honest? I'm still really anxious about money. I'm working, but it's contract stuff, and I don't know what happens when this ends. Some days I feel okay, and some days I feel like I made terrible decisions."

You're not lying in the ascending version. You're appropriately gauging context and adjusting depth.

[RESEARCH: Conversational Depth Regulation | Howard Giles's Communication Accommodation Theory describes how we keep adjusting our speech (pace, formality, vocabulary) towards or away from the person in front of us. Converging, when it's welcome, tends to increase liking; overdoing it can come across as patronising. Deborah Tannen's work on conversational style (1984) shows how two people with different default styles can misread each other even when neither is doing anything wrong. In our view, starting with a lighter version of a story and deepening only when the context supports it isn't inauthenticity. It's a kind of accommodation, part of the ordinary machinery of talk.]

**The Matrix: Pattern × Context**

| Context | Ascending | Descending | Oscillating |
|---------|-----------|------------|-------------|
| **Small talk** | ✅ Perfect | ❌ Too much | ⚠️ Probably too deep |
| **Job interview** | ⚠️ Feels fake if overused | ❌ Signals you're stuck | ✅ Demonstrates resilience |
| **First date** | ⚠️ Feels performative | ❌ Oversharing | ✅ Authentic + boundaried |
| **Close friend** | ⚠️ Feels like you're hiding | ✅ Appropriate if seeking support | ✅ Appropriate always |
| **Professional talk** | ⚠️ Low credibility | ❌ Too heavy | ✅ Teaching moment |
| **Parenting** | ⚠️ Unrealistic model | ⚠️ Too much for kids | ✅ A fuller, more honest story |

The key: **Match your pattern to your audience, purpose, and relationship depth.**`,

          practicalApplication: `**Pattern-Context Matching Practice**

Think of three different stories you tell about yourself. For each one, identify:

1. **What pattern do you currently use?** (Ascending, descending, oscillating)
2. **What contexts do you tell it in?** (Professional, social, intimate)
3. **Is the pattern appropriate for those contexts?**

If not, rewrite the story in a different pattern:

- If you're telling a descending story in professional contexts → try oscillating
- If you're telling only ascending stories to close friends → try oscillating or appropriate descending
- If you're telling oscillating stories in small talk → try ascending (save depth for when it's wanted)

You're building pattern flexibility: the ability to tell the same story in different shapes depending on the room.`,
        },
      },

      {
        id: 'length-adaptation',
        title: 'Story Length Adaptation: 2 Minutes, 5 Minutes, 10 Minutes',
        duration: '8 minutes',
        content: {
          intro: `Picture someone trying to tell a ten-minute story in a two-minute window at a networking event.

They start strong: "So three years ago, I decided to leave corporate consulting to start a non-profit..."

Then they panic. They can feel they're running out of time. So they start talking faster. And faster. Words tumbling over each other. "And then we got funding but the board disagreed and we had to pivot and then COVID hit and we lost volunteers and then we figured out online delivery and then..."

The person they're talking to is smiling politely but their eyes have glazed over. The story isn't landing. Not because it's bad: it's a genuinely interesting story. But because they're trying to compress ten minutes of detail into two minutes of time by talking faster.

That's not how story length works.

The same story can be told brilliantly in 2 minutes or 10 minutes. But you have to know **which parts to expand and which to compress** based on how much time you actually have.

Most people do this backwards. They try to fit everything in by speeding up. That doesn't work. You need different architecture for different lengths.`,

          mainContent: `**The Same Story at Three Different Lengths**

Let's use the "failed business" story from earlier and map it at 2 minutes, 5 minutes, and 10 minutes.

Those lengths are the time the moment gives you, not nonstop talking. At a normal speaking pace (about 130 to 150 words a minute), each example below takes well under its slot, which leaves room for the other person to react, laugh and ask questions.

---

**VERSION 1: The 2-Minute Version (Networking Event)**

**Context:** You've just met someone at a professional event. They ask, "What's your background?"

**Structure:**
- **Setup:** 1-2 sentences (orient quickly)
- **Catalyst:** 1 sentence (what happened)
- **Tension:** 2-3 sentences (compress to key obstacle)
- **Climax:** 1 sentence (the shift)
- **Resolution:** 2-3 sentences (where you are now + learning)

**How it sounds:**

"I spent eight years in corporate strategy before starting a sustainable packaging business. [SETUP] I built it for 18 months, secured clients, but underestimated cash flow timing in B2B sales. [CATALYST + TENSION compressed] By month 18, I had to wind it down strategically. [CLIMAX] That was three years ago. I'm back in corporate strategy now, but I understand cash flow and customer development in a way I never did before. Those 18 months taught me more than eight years of consulting. [RESOLUTION]"

**Word count:** about 75 words
**Duration:** about 30 to 35 seconds spoken, which leaves most of the two minutes for their reply and questions

**What you're doing:**
- Hitting all five structural elements (setup, catalyst, tension, climax, resolution)
- Compressing ruthlessly (no detailed obstacles, no emotional processing)
- Landing on learning (so it's not just "I failed")
- Keeping forward momentum (no pauses, no tangents)

**Common 2-minute mistakes:**
❌ Trying to include too much detail ("And then in month 3, I was in my garage...")
❌ Starting too far back ("So I grew up in a family of entrepreneurs...")
❌ Getting stuck in tension ("I applied to 30 jobs, then 40, then I tried LinkedIn...")

---

**VERSION 2: The 5-Minute Version (Dinner Party)**

**Context:** You're at dinner, someone asks about your career path, and there's genuine interest.

**Structure:**
- **Setup:** 3-4 sentences (set the scene with texture)
- **Catalyst:** 2-3 sentences (make it specific)
- **Tension:** This is where you expand: 1-2 minutes of obstacles, attempts, human details
- **Climax:** 2-3 sentences (make the peak moment vivid)
- **Resolution:** 3-4 sentences (honest, self-aware, some humour)

**How it sounds:**

"So, five years ago, I had what I can only describe as entrepreneurial delusion. I was in corporate strategy (comfortable salary, clear path forward) and I became convinced I was going to revolutionise sustainable packaging. My partner's face when I announced I was quitting was... memorable. [SETUP]

I left the job, cashed out my savings, and started building the business. For the first six months, I felt like I'd finally figured out my purpose. I was working 80-hour weeks, but I loved it. [CATALYST]

Then month six hit and I started realising how little I actually knew. I had clients, and that part was working, but they paid on 60-day terms and I had immediate costs. So I'd deliver a project, send an invoice, and then spend two months chasing payment whilst trying to fund the next project. I was constantly haemorrhaging cash.

Month twelve, I'm sitting in my garage at 2am trying to work out if I can make payroll with a credit card. My neighbour genuinely thought I was building something illegal based on the hours I was keeping.

Month sixteen, I finally admitted to myself it wasn't working. I had maybe three months of runway left. [TENSION]

My accountant, who'd been giving me increasingly worried looks, very gently said, 'You know this isn't sustainable, right?' And I just... knew. I'd known for months, but hearing someone else say it made it real. [CLIMAX]

I wound the business down strategically. That was three years ago. I'm back in corporate strategy now, which some days feels like I failed spectacularly and gave up. But I learnt more in those 18 months than in eight years of consulting. And my garage still smells like cardboard, so I'm constantly reminded of my hubris. [RESOLUTION]"

**Word count:** about 285 words
**Duration:** about 2 minutes spoken; reactions, laughter and questions fill the rest of the five minutes

**What you're doing:**
- Adding human details (partner's face, 2am garage, neighbour's suspicion)
- Expanding tension with specific obstacles (60-day payment terms, credit card payroll)
- Including humour (entrepreneurial delusion, garage still smells like cardboard)
- Making yourself relatable (admitting you knew but didn't want to face it)

**Common 5-minute mistakes:**
❌ Spending too long on setup (3 minutes of backstory before the story starts)
❌ Under-developing tension (still compressing like it's a 2-minute version)
❌ Losing narrative thread (going off on tangents that don't serve the story)

---

**VERSION 3: The 10-Minute Version (Close Friend, Intimate Conversation)**

**Context:** You're with a close friend who knows this story but you're processing it more deeply.

**Structure:**
- **Setup:** 1-2 minutes (emotional context, what you were hoping for)
- **Catalyst:** 1 minute (the decision, the feelings around it)
- **Tension:** 4-5 minutes (this is where you live: detailed obstacles, emotional honesty, multiple attempts)
- **Climax:** 1-2 minutes (the moment of realisation, how it felt)
- **Resolution:** 2-3 minutes (where you are now, what you're still processing)

**How it sounds:**

"You know how I've been thinking about the business again lately? I think it's because it's been three years and I'm realising I haven't fully processed it.

When I left corporate, I felt like I was finally becoming who I was supposed to be. Like, for the first time in my adult life, I wasn't performing someone else's version of success. I was doing something that mattered to me. [SETUP (emotional context)]

The first six months were incredible. I know I was working insane hours, but I loved it. I'd wake up excited. I felt purposeful. [CATALYST (the high before the fall)]

But then cracks started showing. Month six, I landed a big client, delivered the work, sent the invoice... and then waited. And waited. They paid on 60-day terms, which I'd agreed to because I was desperate for the credibility. But I had costs *now*: materials, contractors, my own living expenses.

Month eight, I'm juggling three projects, all in different stages, all with delayed payment, and I'm using a credit card to cover costs. I kept thinking, 'This is just temporary. Once the payments come through, I'll catch up.' But the payments would come through and immediately go to cover the debt I'd accrued waiting for them.

Month twelve, I'm lying awake at 3am doing the same calculation over and over: how long can I sustain this? I wasn't seeing friends because I was working all the time and I was too anxious to be social anyway. My relationship was strained because I was stressed and unavailable.

Month fourteen, I had a panic attack in the shower. Just full-body panic. And I remember thinking, 'This is supposed to be my dream. Why does it feel like it's killing me?'

Month sixteen, I was sitting with my accountant and she said, 'You're not going to make it to month twenty without external funding or debt.' And I knew I didn't want either. I didn't want to owe investors or owe banks. But admitting that felt like admitting I'd failed at the thing I thought was my purpose. [TENSION (detailed, emotionally honest)]

The moment I actually decided to close it was sitting in my car after that meeting. I just sat there for an hour. And I felt this weird mix of devastation and relief. Like, 'It's over. I can stop fighting.' [CLIMAX (the emotional peak)]

I wound it down over two months. Finished existing projects, paid everyone I owed, closed the accounts. Then I went back to corporate strategy.

And I'm still working through this: I'm proud I tried. I genuinely am. But I also feel like I failed at the thing that was supposed to define me. When I see other people's businesses succeeding, I think, 'Why couldn't I make it work? Was I not smart enough? Not resilient enough?'

I know intellectually that most businesses fail, that cash flow is a common issue, that I learnt a tonne. But emotionally, I still feel... stuck in it somehow. Like I haven't fully let go of who I thought I'd become. [RESOLUTION (honest, ongoing, unresolved)]

Does that make sense?"

**Word count:** about 500 words
**Duration:** about 3.5 to 4 minutes spoken; pauses and your friend's responses fill the rest of the ten minutes

**What you're doing:**
- Deep emotional honesty (panic attack, devastation and relief, ongoing feelings of failure)
- Detailed tension with multiple beats (months 6, 8, 12, 14 and 16, showing escalation)
- Vulnerability without resolution ("I'm still working through this")
- Inviting connection ("Does that make sense?")

**Common 10-minute mistakes:**
❌ Losing structure (rambling without narrative arc)
❌ Over-explaining context (spending 5 minutes on setup)
❌ Repeating yourself (saying the same emotional point three different ways)

---

**The Length Adaptation Principle:**

**2 minutes:** Touch all five elements, compress ruthlessly
**5 minutes:** Expand tension with human details and humour
**10 minutes:** Live in tension, deepen emotional honesty

The mistake most people make: they try to tell a 10-minute story in 2 minutes by talking faster. Instead, you need to **architecturally redesign the story** for each length.

[INSIGHT: Length Signals Intimacy | This one is our observation rather than a research finding, and it's worth stating plainly so you can test it against your own experience. Length is a claim on someone's attention. A ten-minute story says, without saying it, "I trust you with this much of me, and I think you have room for it." When somebody gives you that time and stays with you, they're answering in the same currency. Which is also why length is the first thing to get wrong: a ten-minute story to someone who offered you two minutes isn't depth, it's a misread.]`,

          practicalApplication: `**Length Adaptation Practice**

Take one story you tell about yourself and map it at three different lengths:

**2-Minute Version:**
- Setup: 1-2 sentences
- Catalyst: 1 sentence
- Tension: 2-3 sentences (key obstacle only)
- Climax: 1 sentence
- Resolution: 2-3 sentences

Write it out. Time yourself reading it aloud. Does it come in at about 75 to 120 words (under a minute of talking)? Does it have forward momentum?

**5-Minute Version:**
- Setup: 3-4 sentences (add texture)
- Catalyst: 2-3 sentences (make it vivid)
- Tension: 1-2 minutes (expand with specific obstacles, human details)
- Climax: 2-3 sentences (make the moment vivid)
- Resolution: 3-4 sentences (add humour or self-awareness)

Write it out. Time yourself. Does it feel complete without rushing?

**10-Minute Version:**
- Setup: 1-2 minutes (emotional context)
- Catalyst: 1 minute
- Tension: 4-5 minutes (detailed, emotionally honest)
- Climax: 1-2 minutes
- Resolution: 2-3 minutes (unresolved is okay)

This might feel vulnerable to write. That's appropriate for this length.

Now you have three versions of the same story, architecturally designed for different contexts and time constraints.`,
        },
      },

      {
        id: 'reading-room',
        title: 'Reading the Room: When to Tell Which Story',
        duration: '6 minutes',
        content: {
          intro: `Picture a work lunch where a new colleague launches into a detailed story about his divorce. Uncomfortable details. Emotional processing. Ten minutes of therapy-level vulnerability at a table of people eating sandwiches who barely know him.

Everyone keeps eating. Making sympathetic noises. But inside, every person at the table is thinking the same thing: "Why are you telling us this? We're colleagues. We've worked together for three weeks."

He isn't reading the room. He has a story he needs to tell (which is fair: everyone needs to process a divorce), but he's chosen the wrong room, wrong context, wrong audience.

Now picture him later that week, at the same lunch table, telling a completely different story. This one is about accidentally dyeing his dog pink whilst trying to give her a bath. It's light, self-deprecating, absurd. People laugh. He gets good social connection without oversharing.

Same person. Same lunch setting. One story that crashed, one story that landed.

The difference? He's started reading the room.

You now have the tools: core anatomy, context adaptation, pattern choice, length flexibility.

The final skill is knowing **which story to tell when**, and sometimes when not to tell a story at all.

This is about reading the room. And it's the difference between being a good storyteller and being someone people actually want to listen to.`,

          mainContent: `**The Story Selection Matrix**

When someone asks you a question or creates an opening for a story, you're making rapid calculations:

1. **What are they actually asking for?**
2. **What's the context and relationship depth?**
3. **How much time/attention do I have?**
4. **What do I want them to know about me?**
5. **What serves the conversation, not just me?**

Let's break this down.

---

**1. What Are They Actually Asking For?**

Not every question is an invitation for your full story.

**"How are you?"** (passing colleague in hallway)
- They're saying hello, not requesting an update
- Appropriate response: "Good, thanks! You?" (no story needed)

**"How are you?"** (close friend who knows you've been struggling)
- They're genuinely asking
- Appropriate response: Honest story (2-5 minutes depending on context)

**"Tell me about yourself"** (job interview)
- They want professional narrative, not life story
- Appropriate response: Career story (2-3 minutes, oscillating pattern, emphasise skills/learning)

**"Tell me about yourself"** (first date)
- They want values, personality, connection points
- Appropriate response: Personal story that reveals character (3-5 minutes, oscillating, values-forward)

**The skill:** Distinguish between ritual questions, genuine invitations, and specific prompts.

[INSIGHT: Conversational Repair | "Repair" is a technical term from conversation analysis (Schegloff, Jefferson and Sacks, 1977) for how speakers fix trouble in talk as it happens. Their central finding is a generous one: conversation is organised so that speakers get the first chance to fix their own trouble, and self-repair is far more common than being corrected by someone else. Which means that when you misread a question and over- or under-share, the correction is yours to make and everyone expects it. If you launch into a 10-minute story and realise they wanted small talk, you can repair: "Sorry, I'm going on a bit. Short version is X." Reading the room isn't about never making mistakes; it's about adjusting when you do.]

---

**2. What's the Context and Relationship Depth?**

**Shallow context (networking, small talk, acquaintances):**
- Use ascending narratives (light, positive)
- Keep it short (2 minutes max)
- Focus on relatable, entertaining content
- Don't process trauma or seek emotional support

**Medium context (work colleagues, casual friends, dinner parties):**
- Oscillating narratives work well
- 3-5 minutes appropriate
- Humour and humanity appreciated
- Mild vulnerability okay, deep vulnerability not yet

**Deep context (close friends, long-term partner, therapist):**
- Any pattern appropriate (ascending, descending, oscillating)
- Length can extend (5-10 minutes)
- Emotional honesty expected and valued
- Unresolved stories okay

**The mistake:** Bringing deep-context stories into shallow-context situations.

---

**3. How Much Time/Attention Do I Have?**

**Standing at a party, people moving around:**
- Maximum 2-3 minutes
- If your story needs 10 minutes, save it for sitting down

**Sitting at dinner, undivided attention:**
- 5-10 minutes appropriate if there's genuine interest
- Watch for engagement cues (leaning in, follow-up questions)

**Walking between meetings:**
- 1 minute maximum
- Save longer stories for when you have actual time

**The skill:** Match story length to available attention, not just available time.

---

**4. What Do I Want Them to Know About Me?**

Every story you tell is a form of self-presentation. You're choosing what to reveal.

**Job interview: what to reveal**
- Competence, resilience, learning, problem-solving
- Story choice: professional challenge you overcame (oscillating)

**First date: what to reveal**
- Values, self-awareness, humour, emotional availability
- Story choice: personal story that shows character without oversharing

**Close friend: what to reveal**
- Real struggles, vulnerability, ongoing processing
- Story choice: whatever you actually need support with (any pattern)

**The question:** Does this story serve what I want them to understand about me in this context?

---

**5. What Serves the Conversation, Not Just Me?**

This is the one people forget: **storytelling in conversation is relational, not performative**.

**Signs you're serving the conversation:**
- Your story connects to what was being discussed
- It invites response (they can relate or build on it)
- It's proportional to others' contributions (you're not dominating)
- You're genuinely interested in their response

**Signs you're performing:**
- Your story redirects the conversation to you
- It's disconnected from what was being discussed (you're just waiting to tell it)
- It's significantly longer than others' contributions
- You don't ask follow-up questions after telling it

[RESEARCH: Conversational Turn-Taking | Harvey Sacks, who founded conversation analysis with Emanuel Schegloff and Gail Jefferson, called this move the **second story** (*Lectures on Conversation*, 1992). Someone tells you about their week; you answer with something of yours that rhymes with it. A well-chosen second story isn't self-absorption: it shows the other person how you heard theirs. The failure mode isn't telling your own story. It's telling a longer one, then a longer one after that, and never leaving a gap where theirs could go. That's when a conversation quietly becomes a monologue with an audience.]

**The generosity test:**
After you tell a story, do you create space for others to contribute? Or do you fill every silence with another story?

---

**When NOT to Tell a Story**

Sometimes the most skilled choice is not telling a story at all.

**Don't tell a story when:**

1. **It derails the conversation from someone else's moment**
   - Friend: "I just got promoted!"
   - You: "Oh that reminds me of when I got promoted..." ❌
   - Better: "That's brilliant! Tell me about it. What's the new role?" ✓

2. **You're seeking validation for a story you've told repeatedly**
   - If you've told the same story to the same people many times and still need something from them, you may be processing more than storytelling. That's human, especially after a loss. A journal, or a counsellor or therapist, can give it more room.

3. **The context doesn't support the depth your story requires**
   - Don't tell your trauma story at a work drinks event
   - Don't process your relationship breakdown on a first date
   - Don't share financial stress at a networking event (unless that's specifically the context)

4. **You're using storytelling to avoid listening**
   - If every time someone shares, you respond with your story, you're not connecting. You're competing.

**The skill:** Knowing when silence, questions, or brief responses serve better than stories.

---

**Reading Engagement Cues**

As you're telling a story, watch for cues that indicate whether to continue, compress, or wrap up:

**Green lights (keep going):**
- Leaning in, eye contact
- Nodding, small verbal encouragements ("mm-hmm," "wow," "really?")
- Follow-up questions
- Laughter at humour
- Visible emotional response

**Yellow lights (compress and land soon):**
- Neutral face, polite but not engaged
- Looking around the room
- No follow-up questions
- Checking phone

**Red lights (wrap up immediately):**
- Interrupting with topic change
- Visibly uncomfortable
- Looking for escape
- Changing the subject as soon as you pause

Read these cues loosely: people and cultures differ, and in some cultures looking away is a sign of respect, not disengagement.

If you're getting yellow or red lights, don't push through with your 10-minute version. Compress to the 2-minute version or wrap up entirely.

**The skill:** Adjusting mid-story based on engagement.`,

          practicalApplication: `**Room-Reading Practice**

Over the next week, pay attention to story-telling moments in conversation:

**Before you tell a story, ask yourself:**
1. What are they actually asking for? (ritual, genuine invitation, specific prompt)
2. What's the relationship depth? (shallow, medium, deep)
3. How much time/attention is available?
4. What do I want them to know about me in this context?
5. Does this story serve the conversation or just me?

**While you're telling a story, watch for:**
- Green lights (engagement: keep going)
- Yellow lights (polite disinterest: compress)
- Red lights (discomfort: wrap up)

**After you tell a story, notice:**
- Did you create space for their response?
- Did you ask a follow-up question?
- Was your story proportional to the conversation?

You're building social attunement: the ability to read context and adjust in real-time.

This is advanced storytelling, and it takes practice. But it's the difference between being "someone who tells good stories" and being "someone I love talking with."`,
        },
      },
    ],

    exercises: [
      {
        id: 'story-anatomy-rebuild',
        title: 'Story Anatomy Rebuild',
        type: 'Practice',
        duration: '30 minutes',
        description: 'Take a story that doesn\'t quite land and rebuild it using the five-part structure.',
        instructions: [
          'Choose a story you tell about yourself that sometimes falls flat',
          'Write out how you currently tell it (don\'t edit, just capture your usual version)',
          'Now diagnose: Which structural elements are missing or underdeveloped?',
          '  - Is the setup too long or too vague?',
          '  - Are you skipping tension (jumping from catalyst to resolution)?',
          '  - Is the climax unclear?',
          '  - Does the resolution lack meaning?',
          'Rebuild the story using all five elements deliberately:',
          '  - Setup: What was the "before" state? (2-3 sentences)',
          '  - Catalyst: What disrupted it? (1-2 sentences)',
          '  - Tension: What obstacles, attempts, complications followed? (This should be the longest section)',
          '  - Climax: What was the peak moment or shift? (1-2 sentences)',
          '  - Resolution: What\'s different now? What did you learn? (2-3 sentences)',
          'Read both versions aloud. Notice the difference in momentum and engagement.',
        ],
      },
      {
        id: 'context-adaptation-map',
        title: 'Context Adaptation Mapping',
        type: 'Reflection',
        duration: '45 minutes',
        description: 'Map one story across five different contexts to build adaptation flexibility.',
        instructions: [
          'Choose one significant story from your life (career change, relationship ending, personal challenge, achievement)',
          'Write how you\'d tell this story in FIVE different contexts:',
          '  1. Job interview (2-3 min, oscillating, emphasise learning/competence)',
          '  2. Dinner party (3-5 min, humour/relatability, entertaining)',
          '  3. First date (3-4 min, values/character, authentic but boundaried)',
          '  4. Close friend (5-10 min, emotional honesty, vulnerability)',
          '  5. Professional presentation (5-7 min, teaching/lessons, expertise)',
          'For each version, note:',
          '  - What details do you include/exclude?',
          '  - What tone do you use?',
          '  - What resolution do you land on?',
          '  - What do you want the listener to understand about you?',
          'Reflect: How does the same event create five different stories? Are you comfortable with this flexibility, or does it feel inauthentic?',
        ],
      },
      {
        id: 'room-reading-journal',
        title: 'Room-Reading Journal',
        type: 'Ongoing Practice',
        duration: '10 minutes daily for 1 week',
        description: 'Develop real-time awareness of context, engagement, and story selection.',
        instructions: [
          'For one week, keep a daily journal tracking storytelling moments',
          'Each day, note 2-3 times you told a story or had the opportunity to:',
          '  - What was the context?',
          '  - What story did you tell (or choose not to tell)?',
          '  - What pattern did you use? (ascending, descending, oscillating)',
          '  - What length? (2 min, 5 min, 10 min)',
          '  - How did people respond? (green, yellow, or red lights)',
          '  - Did you read the room accurately?',
          '  - What would you do differently next time?',
          'By day 7, you should notice patterns:',
          '  - Do you over-share in shallow contexts?',
          '  - Do you under-share in deep contexts?',
          '  - Are you better at reading engagement than you thought?',
          '  - Where do you need to build more flexibility?',
        ],
      },
    ],

    keyTakeaways: [
      'A reliable structure for personal stories (the five-part model this course teaches): Setup (before state) → Catalyst (disruption) → Tension (struggle/obstacles) → Climax (peak moment/shift) → Resolution (after state/meaning)',
      'Context determines which parts of your story you emphasise, compress, or skip. The same event should be told differently at a job interview, dinner party, first date, or with a close friend',
      'Strategic pattern choice matters: use ascending narratives for social lubrication, descending for appropriate support-seeking, and oscillating for most situations requiring both honesty and hope',
      'Story length adaptation requires architectural redesign, not just talking faster. A 2-minute version touches all five elements but compresses ruthlessly, whilst a 10-minute version lives in tension with emotional depth',
      'Reading the room is the final skill: watch for engagement cues (green, yellow, red lights), match your story depth to relationship intimacy, and sometimes choose not to tell a story at all',
    ],

    reflectionQuestions: [
      'Think of a story you\'ve told that sometimes lands brilliantly and sometimes falls flat. Can you identify which structural element (setup, catalyst, tension, climax, resolution) is inconsistent or missing?',
      'Are you more comfortable telling stories in shallow contexts (networking, small talk) or deep contexts (close friends, therapy)? What does that reveal about your relationship with vulnerability and social performance?',
      'When you adapt a story for different contexts, does it feel like authentic flexibility or uncomfortable performance? What\'s the difference between contextual adaptation and inauthenticity?',
      'Do you tend to over-share (bringing deep stories into shallow contexts) or under-share (keeping things surface even with people who could hold depth)? What would it look like to recalibrate?',
      'How often do you create space for others\' stories after you tell yours, versus filling silences with more of your own stories? What does that pattern reveal about your relationship with storytelling?',
    ],

    nextSteps: `You've learnt a core story structure and how to adapt it to context. You can now tell the same story in multiple shapes depending on the room.

**Module 5: Moving the Heart to Change the Mind** (⭐ flagship module) takes you deeper into the emotional craft of storytelling: how to use sensory details, emotional arcs, and eudaemonic structure to create stories that don't just inform, but transform.

**Toolkit Suggestion:** Use the **Story Structure Builder** to map your stories onto the five-part anatomy and generate adapted versions for different contexts.`,
  },
};

export default storyModule04Content;
